import React from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import cx from 'classnames';
import TruncateMarkup from 'react-truncate-markup';
import Link from 'next/link';
import { useRouter } from 'next/router';

import CardCarousel from 'components/CardCarousel';
import config from 'config';
import type { ContentCarouselCardData } from 'types/cards';
import stripHTMLFromText from 'utils/stripHTMLFromText';

import Styles from './ContentCarouselCard.module.scss';

interface ContentCarouselCardProps {
  styles?: any;
  data: ContentCarouselCardData;
  useSubtitleForAlt?: boolean;
  trackCardClick?: () => void;
}

const ContentCarouselCard: React.FC<ContentCarouselCardProps> = ({
  data,
  styles = {},
  trackCardClick,
  useSubtitleForAlt = true,
}) => {
  const desktopWidth = useMediaQuery(`(min-width:${config.desktopWidth}px)`);
  const desktopXXLWidth = useMediaQuery(`(min-width:${config.desktopXXLWidth}px)`);
  const smallMobileWidth = useMediaQuery(`(min-width:${config.smallMobileWidth}px)`);
  const router = useRouter();

  let lines = 6;
  if (desktopXXLWidth || desktopWidth) {
    lines = 5;
  } else if (smallMobileWidth) {
    lines = 4;
  }

  const {
    carouselAspectRatio,
    description,
    images,
    link,
    linkAs,
    linkText,
    subtitle,
    title,
    type,
  } = data;

  return (
    <Card className={cx(Styles.contentCarouselCard, { [Styles.supplierCard]: type })} sx={styles}>
      <div data-testid="sl-content-card-detailed" className={Styles.contentCarouselCardInner}>
        <CardCarousel
          imageAlt={useSubtitleForAlt ? subtitle : title}
          images={images?.slice(0, 8)}
          itemAspectRatio={carouselAspectRatio}
          link={link}
          linkAs={linkAs}
          segmentEvent={trackCardClick}
        />
        <CardContent className={Styles.cardContent}>
          <div className={Styles.contentCarouselCardHeaderWrap}>
            <div className={Styles.contentCarouselCardHeader}>
              <div>
                <Typography
                  className={Styles.contentCarouselCardTitle}
                  variant={description ? 'title2' : 'footnote'}
                >
                  {title}
                </Typography>
                <div className={Styles.contentCarouselCardSubtitleWrap}>
                  <div className={Styles.contentCarouselCardSubtitle}>
                    <Tooltip arrow placement="top" title={subtitle || ''}>
                      <Typography variant="title3">{subtitle}</Typography>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {description && (
            <div className={Styles.descriptionWrap}>
              <TruncateMarkup lines={lines}>
                <Typography variant="body" color="text.primary">
                  {stripHTMLFromText(description)}
                </Typography>
              </TruncateMarkup>
            </div>
          )}
          {linkText && link && (
            <CardActions className={Styles.contentCarouselCardActions}>
              {router ? (
                <Link href={link} as={linkAs} shallow={!!linkAs}>
                  <Button
                    classes={{
                      root: Styles.cardPriceButton,
                    }}
                    color="secondary"
                    disableFocusRipple
                    disableRipple
                    size="medium"
                    style={{ textDecoration: 'none' }}
                    variant="secondary"
                    onClick={trackCardClick}
                  >
                    <Typography variant="callout2">{linkText}</Typography>
                  </Button>
                </Link>
              ) : (
                <Button
                  classes={{
                    root: Styles.cardPriceButton,
                  }}
                  color="secondary"
                  disableFocusRipple
                  disableRipple
                  onClick={trackCardClick}
                  href={link}
                  size="medium"
                  style={{ textDecoration: 'none' }}
                  variant="secondary"
                >
                  <Typography variant="callout2">{linkText}</Typography>
                </Button>
              )}
            </CardActions>
          )}
        </CardContent>
      </div>
      {router ? (
        <Link
          href={link}
          as={linkAs}
          shallow={!!linkAs}
          className={Styles.cardLink}
          aria-label={`View ${title}`}
          onClick={trackCardClick}
        />
      ) : (
        <a
          className={Styles.cardLink}
          aria-label={`View ${title}`}
          href={link}
          onClick={trackCardClick}
        />
      )}
    </Card>
  );
};

export default ContentCarouselCard;
